import { sdk } from '@/services/sdk';
import { rows } from '@/shared/data';
import { isPaidLine, normUsername } from '@/shared/referrals';

const { isNotNull } = window.ApperSDK.CoreSDK;

/** Path prefix for referral links: /r/<username>. */
export const REFERRAL_PATH = '/r';

const STORED_KEY = 'gc:incoming-ref';
const VISITOR_KEY = 'gc:visitor';
const LOGGED_KEY = 'gc:ref-logged';

/** A friend keeps their referral for this long after opening the link. */
const STORED_DAYS = 30;

/** The shareable link for a username, on whatever domain the store is running on. */
export function referralUrl(username) {
  const name = normUsername(username);
  if (!name) return '';
  return `${window.location.origin}${REFERRAL_PATH}/${encodeURIComponent(name)}`;
}

/** The link without https://, for printing on the share image. */
export const shortUrl = (url) => String(url || '').replace(/^https?:\/\//, '');

/** Remember the username a visitor arrived with, so checkout can fill it in later. */
export function storeIncomingRef(username) {
  const name = normUsername(username);
  if (!name) return;
  try {
    window.localStorage.setItem(STORED_KEY, JSON.stringify({ name, at: Date.now() }));
  } catch {
    // private mode: the link still works if they order straight away with ?ref=
  }
}

/** The username this browser arrived with, if it is still fresh. */
export function readStoredRef() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORED_KEY) || 'null');
    if (!saved?.name) return '';
    if (Date.now() - Number(saved.at || 0) > STORED_DAYS * 86400000) return '';
    return normUsername(saved.name);
  } catch {
    return '';
  }
}

export function clearStoredRef() {
  try {
    window.localStorage.removeItem(STORED_KEY);
  } catch {
    // nothing to clear
  }
}

function visitorId() {
  try {
    let id = window.localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
}

/**
 * Record that someone opened a referral link. Logged once per username per browser per day,
 * so refreshing the page does not inflate the count. Never throws.
 */
export async function logReferralVisit(username, landing = '') {
  const name = normUsername(username);
  if (!name) return;
  const today = new Date().toISOString().slice(0, 10);
  const stamp = `${name}|${today}`;
  try {
    if (window.localStorage.getItem(LOGGED_KEY) === stamp) return;
  } catch {
    // log anyway
  }
  try {
    const res = await sdk.table('referral_visits_c').create({
      Name: name,
      landing_c: String(landing || '').slice(0, 250),
      visited_at_c: new Date().toISOString(),
      visitor_c: visitorId(),
    });
    if (res?.success !== false) {
      try {
        window.localStorage.setItem(LOGGED_KEY, stamp);
      } catch {
        // fine
      }
    }
  } catch {
    // tracking is best-effort
  }
}

const startOf = (o) => new Date(o.activated_at_c || o.CreatedOn || 0).getTime();

/** The username on a buyer's first order that has one (oldest first). */
export function firstLineOf(orders) {
  const withName = (orders ?? []).filter((o) => normUsername(o.line_username_c));
  withName.sort((a, b) => startOf(a) - startOf(b) || Number(a.Id) - Number(b.Id));
  return withName[0] ?? null;
}

/**
 * Buyer side: the signed-in buyer's referral username (from the first line they bought),
 * whether that line is live right now, and their link.
 */
export async function loadMyReferral() {
  const res = await sdk
    .table('orders_c')
    .select(['Name', 'status_c', 'ends_at_c', 'line_username_c', 'activated_at_c', 'gift_months_c', 'CreatedOn'])
    .where(isNotNull('line_username_c'))
    .orderBy('CreatedOn')
    .limit(100)
    .fetch();
  const list = rows(res);
  const first = firstLineOf(list);
  if (!first) return { username: '', url: '', live: false, freeMonths: 0, lines: list };
  const username = normUsername(first.line_username_c);
  const live = list.some((o) => normUsername(o.line_username_c) === username && isPaidLine(o));
  const freeMonths = list.reduce((n, o) => n + (Number(o.gift_months_c) || 0), 0);
  return { username, url: referralUrl(username), live, freeMonths, lines: list };
}

/** Text that goes with the link when a buyer shares it. */
export function shareMessage(username, url) {
  return `I watch on Ghost Cable. Grab a plan with my link: ${url} (or type my username ${username} at checkout).`;
}

/** One-tap share targets. Each opens in a new tab. */
export function shareTargets(username, url) {
  const text = shareMessage(username, url);
  const enc = encodeURIComponent;
  return [
    { id: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle', href: `https://wa.me/?text=${enc(text)}` },
    {
      id: 'x',
      label: 'X',
      icon: 'AtSign',
      href: `https://twitter.com/intent/tweet?text=${enc('I watch on Ghost Cable. Grab a plan with my link:')}&url=${enc(url)}`,
    },
    { id: 'facebook', label: 'Facebook', icon: 'ThumbsUp', href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { id: 'sms', label: 'Text', icon: 'MessageSquare', href: `sms:?&body=${enc(text)}` },
    {
      id: 'email',
      label: 'Email',
      icon: 'Mail',
      href: `mailto:?subject=${enc('Try Ghost Cable with my link')}&body=${enc(text)}`,
    },
  ];
}
